/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/features/Auth/state/AuthContext";
import { useIsSuperAdminRole } from "@/shared/hooks/use-system-role";
import { useToast } from "@/shared/hooks/use-toast";
import { get, post } from "@/shared/utils/fetchApi";
import { sameOriginProxyUrl } from "@/shared/utils/proxyUrl";
import { logger } from "@/infrastructure/lib/logger";
import type { Organization } from "@/features/Organization/services/organizationService";

/**
 * Organização ATIVA da sessão. Nasceu como troca de org exclusiva do superadmin e,
 * a partir do AB#84509, é o mesmo mecanismo para qualquer usuário que pertença a mais
 * de uma organização — a diferença está apenas em QUAIS organizações a lista traz:
 * superadmin recebe todas, os demais recebem as suas (o backend escopa `GET
 * /organizations`) e um override sem vínculo é recusado lá.
 *
 * O nome `useSuperAdminOrg` fica como alias porque é o que 30+ call sites importam;
 * `useActiveOrg` é o nome correto do conceito.
 */
const STORAGE_KEY = "prodgy_active_organization";
// Chave anterior, só para não perder a seleção de quem já estava logado.
const LEGACY_STORAGE_KEY = "prodgy_superadmin_selected_org";

/** Convite pendente endereçado ao usuário autenticado (AB#84509). */
export interface PendingOrgInvite {
  id: string;
  token: string;
  organization_id: string;
  organization_name: string;
}

interface SuperAdminOrgContextType {
  /** Currently selected organization (null if none selected yet) */
  selectedOrganization: Organization | null;
  /** Organizations the user can act as: all of them for superadmin, their own otherwise */
  organizations: Organization[];
  /** Whether organizations are being loaded */
  isLoading: boolean;
  /** Switch to a different organization */
  setSelectedOrganization: (org: Organization) => void;
  /** Clear selection (back to platform scope, superadmin only) */
  clearSelectedOrganization: () => void;
  /** Reload organizations list and reset to default org */
  reloadOrganizations: () => Promise<void>;
  /** Convites de organização aguardando aceite — o convite não depende mais do email */
  pendingInvites: PendingOrgInvite[];
  /** Aceita o convite e devolve a organização recém-liberada */
  acceptInvite: (invite: PendingOrgInvite) => Promise<void>;
  /**
   * Nome da organização recém-aceita, enquanto o aviso do seletor não é dispensado.
   * Existe só para explicar a troca de organização a quem acabou de ganhar a segunda —
   * é estado de momento, não preferência: some ao ser dispensado e não volta.
   */
  justJoinedOrganization: string | null;
  clearJustJoinedOrganization: () => void;
}

const SuperAdminOrgContext = createContext<
  SuperAdminOrgContextType | undefined
>(undefined);

/**
 * Role do usuário na organização ATIVA, como o backend a resolve (AB#84509), ou null
 * quando não há organização ativa / a lista ainda não chegou.
 *
 * Não usa `useSuperAdminOrg` porque este hook é consultado por telas montadas fora do
 * provider (fluxos de auth e onboarding), onde a versão que lança derrubaria a tela.
 * Fora do provider a resposta é null e o chamador cai na role global do perfil.
 */
export function useActiveOrgRole(): string | null {
  const context = useContext(SuperAdminOrgContext);
  return context?.selectedOrganization?.role ?? null;
}

/**
 * Id da organização ativa, ou null fora do provider / antes da lista chegar.
 *
 * Serve principalmente para compor `queryKey` do react-query: uma consulta escopada
 * por organização que não carregue este id na chave continua servindo o resultado da
 * organização anterior depois da troca — e, como o header vai correto, o dado errado
 * chega sem erro nenhum. Mesma razão de `useActiveOrgRole` para não lançar fora do
 * provider.
 */
export function useActiveOrgId(): string | null {
  const context = useContext(SuperAdminOrgContext);
  return context?.selectedOrganization?.id ?? null;
}

export function SuperAdminOrgProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, user } = useAuth();
  const { isSuperAdmin } = useIsSuperAdminRole();
  const { toast } = useToast();
  const { t } = useTranslation();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrganization, setSelectedOrgState] =
    useState<Organization | null>(null);
  // Inicia como true: no refresh o scope depende da org selecionada do superadmin,
  // que só é conhecida após o fetch. Mantê-lo false no 1º render permitiria que o
  // WorkspaceGuard redirecionasse antes da resolução. O caminho não-superadmin/
  // não-autenticado abaixo desliga o loading imediatamente.
  const [isLoading, setIsLoading] = useState(true);
  const [pendingInvites, setPendingInvites] = useState<PendingOrgInvite[]>([]);
  const [justJoinedOrganization, setJustJoinedOrganization] = useState<
    string | null
  >(null);

  // Lidos dentro do fetch sem entrar nas deps: mudança de role/home org já dispara o
  // efeito abaixo, e colocá-los como dependência refaria o fetch a cada render do auth.
  const isSuperAdminRef = useRef(isSuperAdmin);
  const homeOrgRef = useRef<string | null | undefined>(user?.organizationId);
  isSuperAdminRef.current = isSuperAdmin;
  homeOrgRef.current = user?.organizationId;

  const setSelectedOrganization = useCallback((org: Organization) => {
    setSelectedOrgState(org);
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ id: org.id, name: org.name }),
    );
    localStorage.removeItem(LEGACY_STORAGE_KEY);
  }, []);

  const clearSelectedOrganization = useCallback(() => {
    setSelectedOrgState(null);
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(LEGACY_STORAGE_KEY);
  }, []);

  /** Busca a lista sem tocar em estado — reusada pelo aceite de convite. */
  const fetchOrganizations = useCallback(async (): Promise<Organization[]> => {
    // v2: GET /organizations retorna Organization[] direto; legacy: { data: [...] }
    const rawOrgs = await get<Organization[]>("/organizations");
    // Normaliza logo_url para caminho same-origin (igual getOrganizationById/Details),
    // senão o host embutido no upload dá mixed content / loopback block e a logo não renderiza.
    return rawOrgs.map((o) => ({
      ...o,
      logo_url: sameOriginProxyUrl(o.logo_url),
    }));
  }, []);

  const fetchAndSetOrganizations = useCallback(
    async (restoreFromStorage = true) => {
      setIsLoading(true);
      try {
        const orgs = await fetchOrganizations();
        setOrganizations(orgs);

        if (restoreFromStorage) {
          const stored =
            localStorage.getItem(STORAGE_KEY) ??
            localStorage.getItem(LEGACY_STORAGE_KEY);
          if (stored) {
            try {
              const parsed = JSON.parse(stored);
              const found = orgs.find((o: Organization) => o.id === parsed.id);
              if (found) {
                setSelectedOrgState(found);
                // Migra quem veio pela chave antiga: sem isto ela sobrevive indefinidamente
                // e passam a existir duas fontes para a mesma resposta.
                localStorage.setItem(
                  STORAGE_KEY,
                  JSON.stringify({ id: found.id, name: found.name }),
                );
                localStorage.removeItem(LEGACY_STORAGE_KEY);
                setIsLoading(false);
                return;
              }
            } catch {
              /* seleção corrompida cai no default abaixo */
            }
            localStorage.removeItem(STORAGE_KEY);
            localStorage.removeItem(LEGACY_STORAGE_KEY);
          }
        }

        // Superadmin abre em escopo de plataforma (sem org) até escolher uma. Os demais
        // abrem na home org do perfil — sem seleção nenhuma eles não veriam workspace
        // algum, e "plataforma" não é um escopo que exista para quem não é superadmin.
        if (isSuperAdminRef.current) {
          setSelectedOrgState(null);
          return;
        }
        const home =
          orgs.find((o) => o.id === homeOrgRef.current) ?? orgs[0] ?? null;
        setSelectedOrgState(home);
        if (home)
          localStorage.setItem(
            STORAGE_KEY,
            JSON.stringify({ id: home.id, name: home.name }),
          );
      } catch {
        toast({
          title: "Erro",
          description: "Não foi possível carregar a lista de organizações.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    },
    [toast, fetchOrganizations],
  );

  const reloadOrganizations = useCallback(async () => {
    localStorage.removeItem(STORAGE_KEY);
    // `fetchApi` ainda usa a chave antiga como fallback ao montar o header: deixá-la
    // aqui manteria as requisições escopadas na organização que a UI já esqueceu.
    localStorage.removeItem(LEGACY_STORAGE_KEY);
    await fetchAndSetOrganizations(false);
  }, [fetchAndSetOrganizations]);

  const loadPendingInvites = useCallback(async () => {
    // `GET /invites/pending` existe na api (apps/api/start/routes/invites.ts:17).
    // Falha aqui não derruba o seletor: a lista fica vazia e a seção não aparece.
    try {
      const res = await get<{ data: PendingOrgInvite[] }>("/invites/pending");
      setPendingInvites(res?.data ?? []);
    } catch {
      // Convite pendente é um extra na tela: falhar aqui não pode derrubar o seletor.
      setPendingInvites([]);
    }
  }, []);

  const acceptInvite = useCallback(
    async (invite: PendingOrgInvite) => {
      await post("/invites/accept", { inviteToken: invite.token });
      setPendingInvites((current) => current.filter((i) => i.id !== invite.id));

      // A organização entra na lista e vira a ativa IMEDIATAMENTE, com o que o próprio
      // convite já informa (id e nome). Esperar a lista completa do servidor deixava a
      // tela parada entre o clique e o seletor aparecer — o que parecia travamento.
      const accepted = {
        id: invite.organization_id,
        name: invite.organization_name,
        logo_url: null,
        // O convite não diz qual role foi concedida. Assumir 'user' aqui é o palpite
        // seguro: errar para menos esconde um menu por um instante, errar para mais
        // oferece administração que o backend vai recusar. A reconciliação corrige.
        role: "user",
      } as Organization;
      setOrganizations((current) =>
        current.some((o) => o.id === accepted.id)
          ? current
          : [...current, accepted],
      );
      setSelectedOrganization(accepted);
      setJustJoinedOrganization(accepted.name);

      // Reconcilia em segundo plano: traz logo, plano, a role real e o que mais o
      // servidor souber. Falha aqui não desfaz o aceite — a organização já é utilizável.
      void fetchOrganizations()
        .then((orgs) => {
          setOrganizations(orgs);
          const real = orgs.find((o) => o.id === accepted.id);
          if (real) setSelectedOrgState(real);
        })
        .catch((err) => {
          // Engolir isto em silêncio deixava a pessoa com a organização provisória:
          // nome certo, mas role assumida como 'user'. Ela veria menos do que tem
          // direito e não teria como saber que bastava recarregar.
          logger.error(
            "[ActiveOrg] falha ao reconciliar a organização aceita",
            {
              err,
              inviteId: invite.id,
            },
          );
          toast({
            title: t(
              "notifications.invite.syncFailed",
              "Organização adicionada",
            ),
            description: t(
              "notifications.invite.syncFailedDescription",
              "Recarregue a página para ver seus acessos completos nela.",
            ),
          });
        });
    },
    [fetchOrganizations, setSelectedOrganization, toast, t],
  );

  const clearJustJoinedOrganization = useCallback(
    () => setJustJoinedOrganization(null),
    [],
  );

  // Carrega as organizações de QUALQUER usuário autenticado (AB#84509): o backend
  // devolve todas para superadmin e apenas as vinculadas para os demais.
  useEffect(() => {
    if (!isAuthenticated) {
      setOrganizations([]);
      setSelectedOrgState(null);
      setPendingInvites([]);
      // Estado de momento também sai no logout: se ficasse, quem entrasse em
      // seguida no mesmo navegador veria o "você entrou em X" de outra pessoa.
      setJustJoinedOrganization(null);
      setIsLoading(false);
      return;
    }
    fetchAndSetOrganizations(true);
    loadPendingInvites();
  }, [
    isAuthenticated,
    isSuperAdmin,
    fetchAndSetOrganizations,
    loadPendingInvites,
  ]);

  return (
    <SuperAdminOrgContext.Provider
      value={{
        selectedOrganization,
        organizations,
        isLoading,
        setSelectedOrganization,
        clearSelectedOrganization,
        reloadOrganizations,
        pendingInvites,
        acceptInvite,
        justJoinedOrganization,
        clearJustJoinedOrganization,
      }}
    >
      {children}
    </SuperAdminOrgContext.Provider>
  );
}

export function useSuperAdminOrg() {
  const context = useContext(SuperAdminOrgContext);
  if (context === undefined) {
    throw new Error(
      "useSuperAdminOrg must be used within a SuperAdminOrgProvider",
    );
  }
  return context;
}

/** Nome correto do conceito a partir do AB#84509 — `useSuperAdminOrg` é o alias histórico. */
export const useActiveOrg = useSuperAdminOrg;
export const ActiveOrgProvider = SuperAdminOrgProvider;

/** @deprecated Use SuperAdminOrgProvider instead */
export const SystemOrgProvider = SuperAdminOrgProvider;
/** @deprecated Use useSuperAdminOrg instead */
export const useSystemOrg = useSuperAdminOrg;
